/**
 * Classification of links that stand for uploaded files. A link becomes an
 * attachment or a video node when its href points at this instance's own
 * file endpoints (or a signed object-storage URL) and the last whitespace
 * separated token of its text is a byte count or a `WIDTHxHEIGHT` pair.
 */

/** Relative endpoints that serve uploaded files. */
const FILE_ENDPOINTS = ["/api/attachments.redirect", "/api/files."];

/** The opt-out: render a plain link to the file instead of a file card. */
export function hasDisplayLink(href: string): boolean {
  return /[?&]display=link\b/.test(href);
}

export function isFileHref(href: string): boolean {
  if (hasDisplayLink(href)) return false;
  if (FILE_ENDPOINTS.some((endpoint) => href.startsWith(endpoint))) return true;
  // Signed object-storage URLs are how shared exports reference their files.
  return /[?&]X-Amz-Signature=/i.test(href);
}

export interface LinkTextParts {
  title: string;
  size: number | null;
  width: number | null;
  height: number | null;
}

/** The final token of a file link's text decides which node it becomes. */
export function parseLinkText(text: string): LinkTextParts {
  const tokens = text.trim().split(/\s+/);
  const last = tokens[tokens.length - 1] ?? "";
  const dimensions = /^(\d+)x(\d+)$/.exec(last);
  if (dimensions) {
    return {
      title: tokens.slice(0, -1).join(" "),
      size: null,
      width: Number(dimensions[1]),
      height: Number(dimensions[2]),
    };
  }
  if (/^\d+$/.test(last) && tokens.length > 1) {
    return { title: tokens.slice(0, -1).join(" "), size: Number(last), width: null, height: null };
  }
  return { title: text.trim(), size: null, width: null, height: null };
}

/**
 * Services whose URLs the editor renders as a rich embed. Detection lives here
 * so the editor and the server agree; the markdown parser deliberately does not
 * promote links to embeds (a bare link round-trips as a link).
 */
const EMBED_PATTERNS: RegExp[] = [
  /^https?:\/\/(www\.)?youtube\.com\/watch\?v=/i,
  /^https?:\/\/youtu\.be\//i,
  /^https?:\/\/(www\.)?vimeo\.com\/\d+/i,
  /^https?:\/\/open\.spotify\.com\//i,
  /^https?:\/\/(www\.)?trello\.com\/[bc]\//i,
  /^https?:\/\/(www\.)?pinterest\.[a-z.]+\//i,
  /^https?:\/\/(www\.)?linkedin\.com\/(posts|embed)\//i,
  /^https?:\/\/(www\.)?dropbox\.com\/s\//i,
  /^https?:\/\/gist\.github\.com\//i,
  /^https?:\/\/gitlab\.com\/[^/]+\/[^/]+\/snippets\//i,
  /^https?:\/\/(\w+\.)?invisionapp\.com\//i,
  /^https?:\/\/(www\.)?jsfiddle\.net\//i,
  /^https?:\/\/(www\.)?berrycast\.com\//i,
  /^https?:\/\/(www\.)?plantuml\.com\//i,
  /^https?:\/\/(app\.)?diagrams\.net\//i,
];

export function isEmbedHref(href: string): boolean {
  return EMBED_PATTERNS.some((pattern) => pattern.test(href));
}

/** Embeds percent-escape underscores on both halves of the link. */
export function escapeEmbedUrl(url: string): string {
  return url.replace(/_/g, "%5F");
}
